<template>
  <div class="enhanced-color-picker">
    <div class="color-display d-flex align-center my-4">
      <v-card
        :style="{ backgroundColor: displayValue }"
        class="color-preview mr-2"
        elevation="0"
        @click="showPicker = !showPicker"
      >
        <!-- Text color indicator -->
        <div
          class="text-color-indicator"
          :style="{ color: textColor }"
        >
          T
        </div>
      </v-card>
      <div class="flex-grow-1">
        <v-text-field
          hide-details
          v-model="displayValue"
          dense
          outlined
          placeholder="#RRGGBB"
          :rules="[validateHexColor]"
          @input="updateColor"
          append-icon="mdi-content-copy"
          @click:append="copyColorToClipboard"
        />
      </div>
    </div>

    <v-dialog
      v-model="showPicker"
      max-width="300"
      content-class="color-picker-dialog"
    >
      <v-card class="color-picker-dialog-card">
        <v-card-text>
          <!-- Predefined colors -->
          <div class="predefined-colors mb-3">
            <div class="text-caption mb-1">
              Predefined Colors
            </div>
            <div class="color-grid">
              <v-card
                v-for="color in predefinedColors"
                :key="color"
                :style="{ backgroundColor: color }"
                :class="['color-swatch', { 'selected': displayValue === color }]"
                elevation="0"
                @click="selectColor(color)"
              />
            </div>
          </div>

          <!-- Hex input field -->
          <div class="hex-input mb-3">
            <div class="text-caption mb-1">
              Hex Color Code
            </div>
            <v-text-field
              v-model="displayValue"
              dense
              outlined
              placeholder="#RRGGBB"
              :rules="[validateHexColor]"
              @input="updateColor"
              hide-details
              class="mb-2"
              append-icon="mdi-content-copy"
              @click:append="copyColorToClipboard"
            />
          </div>

          <!-- Color sliders -->
          <div class="color-sliders">
            <div class="text-caption mb-1">
              Custom Color
            </div>

            <!-- Red slider -->
            <div class="d-flex align-center mb-2">
              <span class="slider-label">R</span>
              <v-slider
                v-model="red"
                min="0"
                max="255"
                hide-details
                class="mx-2"
                @input="updateFromRGB"
              />
              <span class="slider-value">{{ red }}</span>
            </div>

            <!-- Green slider -->
            <div class="d-flex align-center mb-2">
              <span class="slider-label">G</span>
              <v-slider
                v-model="green"
                min="0"
                max="255"
                hide-details
                class="mx-2"
                @input="updateFromRGB"
              />
              <span class="slider-value">{{ green }}</span>
            </div>

            <!-- Blue slider -->
            <div class="d-flex align-center mb-2">
              <span class="slider-label">B</span>
              <v-slider
                v-model="blue"
                min="0"
                max="255"
                hide-details
                class="mx-2"
                @input="updateFromRGB"
              />
              <span class="slider-value">{{ blue }}</span>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showPicker = false"
          >
            Close
          </v-btn>
          <v-btn
            color="primary"
            @click="applyColor"
          >
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'EnhancedColorPicker',
  props: {
    value: {
      type: [String, Object],
      default: '#FFFFFF',
      required: false
    },
    required: {
      type: Boolean,
      default: false
    },
    textColorValue: {
      type: String,
      default: 'white'
    }
  },
  data () {
    return {
      displayValue: this.value && typeof this.value === 'string' ? this.value : '#FFFFFF',
      showPicker: false,
      red: 255,
      green: 255,
      blue: 255,
      contrastWarning: false,
      predefinedColors: [
        '#1976D2', '#2196F3', '#0D47A1', '#311B92', '#4527A0', '#512DA8',
        '#673AB7', '#7B1FA2', '#9C27B0', '#C2185B', '#D32F2F', '#F44336',
        '#EF5350', '#EC407A', '#AB47BC', '#7E57C2', '#5C6BC0', '#42A5F5',
        '#29B6F6', '#26C6DA', '#26A69A', '#66BB6A', '#9CCC65', '#D4E157',
        '#FFEE58', '#FFCA28', '#FFA726', '#FF7043', '#8D6E63', '#78909C'
      ]
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newValue) {
        if (newValue && typeof newValue === 'string') {
          this.displayValue = newValue
          this.updateRGBFromHex(newValue)
        }
      }
    }
  },
  computed: {
    contrastRatio () {
      // Calculate contrast ratio with white text
      return this.calculateContrastRatio(this.displayValue, '#FFFFFF')
    },
    hasGoodContrast () {
      // WCAG AA requires a contrast ratio of at least 4.5:1 for normal text
      return this.contrastRatio >= 4.5
    },
    textColor () {
      // Calculate contrast with white and black text
      const whiteContrast = this.calculateContrastRatio(this.displayValue, '#FFFFFF')
      const blackContrast = this.calculateContrastRatio(this.displayValue, '#000000')

      // Return the color with better contrast
      return whiteContrast > blackContrast ? 'white' : 'black'
    }
  },
  methods: {
    validateHexColor (value) {
      return /^#([0-9A-F]{3}){1,2}$/i.test(value) || 'Invalid hex color'
    },
    updateColor (value) {
      if (this.validateHexColor(value) === true) {
        this.updateRGBFromHex(value)
        this.checkContrast(value)
        this.$emit('input', value)
        this.$emit('update:text-color', this.textColor)
      }
    },
    selectColor (color) {
      this.displayValue = color
      this.updateRGBFromHex(color)
      this.checkContrast(color)
      this.$emit('input', color)
      this.$emit('update:text-color', this.textColor)
    },
    updateRGBFromHex (hex) {
      // Remove # if present
      hex = hex.replace('#', '')

      // Handle shorthand hex (#RGB)
      if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
      }

      this.red = parseInt(hex.substring(0, 2), 16)
      this.green = parseInt(hex.substring(2, 4), 16)
      this.blue = parseInt(hex.substring(4, 6), 16)
    },
    updateFromRGB () {
      const hex = '#' +
          this.componentToHex(this.red) +
          this.componentToHex(this.green) +
          this.componentToHex(this.blue)

      this.displayValue = hex
      this.checkContrast(hex)
      this.$emit('input', hex)
      this.$emit('update:text-color', this.textColor)
    },
    componentToHex (c) {
      const hex = c.toString(16)
      return hex.length === 1 ? '0' + hex : hex
    },
    applyColor () {
      this.$emit('input', this.displayValue)
      this.$emit('update:text-color', this.textColor)
      this.showPicker = false
    },
    checkContrast (color) {
      // Calculate contrast with white text
      const ratio = this.calculateContrastRatio(color, '#FFFFFF')
      this.contrastWarning = ratio < 4.5
    },
    calculateContrastRatio (bgColor, textColor) {
      const bgLuminance = this.getLuminance(bgColor)
      const textLuminance = this.getLuminance(textColor)

      // Calculate contrast ratio according to WCAG formula
      const ratio = (Math.max(bgLuminance, textLuminance) + 0.05) /
          (Math.min(bgLuminance, textLuminance) + 0.05)

      return ratio
    },
    getLuminance (hexColor) {
      // Remove # if present
      hexColor = hexColor.replace('#', '')

      // Handle shorthand hex (#RGB)
      if (hexColor.length === 3) {
        hexColor = hexColor[0] + hexColor[0] + hexColor[1] + hexColor[1] + hexColor[2] + hexColor[2]
      }

      // Convert hex to RGB
      const r = parseInt(hexColor.substring(0, 2), 16) / 255
      const g = parseInt(hexColor.substring(2, 4), 16) / 255
      const b = parseInt(hexColor.substring(4, 6), 16) / 255

      // Calculate luminance according to WCAG formula
      const R = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
      const G = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
      const B = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)

      return 0.2126 * R + 0.7152 * G + 0.0722 * B
    },
    copyColorToClipboard () {
      // Copy the current color value to clipboard
      navigator.clipboard.writeText(this.displayValue)
        .then(() => {
          // Show a snackbar or some other notification if desired
          this.$emit('copied', this.displayValue)
        })
        .catch(err => {
          console.error('Could not copy text: ', err)
        })
    }
  }
}
</script>

<style scoped>
.enhanced-color-picker {
  position: relative;
  width: 100%;
}

.color-preview {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  border: 1px solid #ddd;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.color-picker-dialog {
  z-index: 10;
}

.color-picker-dialog-card {
  padding: 8px;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
}

.color-swatch {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #ddd;
  transition: transform 0.2s;
}

.color-swatch:hover {
  transform: scale(1.1);
}

.color-swatch.selected {
  border: 2px solid #000;
}

.slider-label {
  width: 20px;
  text-align: center;
}

.slider-value {
  width: 40px;
  text-align: right;
}

.text-color-indicator {
  font-weight: bold;
  font-size: 18px;
  text-shadow: 0 0 2px rgba(128, 128, 128, 0.5);
}
</style>
